import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';

const STORAGE_KEY = 'appConfig';

export interface TelematicsConfig {
  /**
   * Manual Optifleet refresh window ('1h' | '6h' | '12h' | '24h'), set via
   * Configuration > Control Centre > Optifleet Manual Refresh Range.
   * That UI is org-scoped (org123, or any Cognito admin). Only affects the legacy
   * Optifleet SSE fetch (FleetDataHub's fetchApiData) — the live Control Centre
   * poll (Samsara /vehicles/live) is a fixed 5-minute poll and does not read this field.
   */
  latestIntervalDisplay: string;
}

export interface ProfitabilityConfig {
  /** Apply fuel/standing rates by body style or tonnage class. */
  costRateBasis: 'body_style' | 'tonnage';
  mileageRate3_5T: number;
  mileageRate7_5T: number;
  mileageRate18T: number;
  mileageRate26T: number;
  mileageRateArtic: number;
  hourlyRate3_5T: number;
  hourlyRate7_5T: number;
  hourlyRate18T: number;
  hourlyRate26T: number;
  hourlyRateArtic: number;
  /** Working (driving) hour rate £/hr per vehicle type; used with driving_hours from telematics */
  workingHourlyRate3_5T: number;
  workingHourlyRate7_5T: number;
  workingHourlyRate18T: number;
  workingHourlyRate26T: number;
  workingHourlyRateArtic: number;
  /** Legacy fallback when per-vehicle working rate not set */
  workingHourlyRate: number;
  maxDailyHours: number;
  /** Fuel cost £/mile by body style */
  fuelCostLorry: number;
  fuelCostTractorUnit: number;
  fuelCostMiniTruck: number;
  fuelCostServiceVan: number;
  fuelCostRigidTruck: number;
  fuelCostDefault: number;
  /** Fuel cost £/mile by tonnage (when costRateBasis = tonnage) */
  fuelCost3_5T: number;
  fuelCost7_5T: number;
  fuelCost18T: number;
  fuelCost26T: number;
  fuelCostArtic: number;
  /** Depreciation cost £/day by body style */
  depreciationLorry: number;
  depreciationTractorUnit: number;
  depreciationMiniTruck: number;
  depreciationServiceVan: number;
  depreciationRigidTruck: number;
  depreciationDefault: number;
  /** Standing £/day by tonnage */
  depreciation3_5T: number;
  depreciation7_5T: number;
  depreciation18T: number;
  depreciation26T: number;
  depreciationArtic: number;
}

/** Email / automation preferences for the profitability executive PDF (UI + local persistence; delivery requires backend). */
export interface ProfitabilityReportScheduleConfig {
  enabled: boolean;
  recipientEmail: string;
  /** Optional recipient name for personalisation */
  recipientName: string;
  /** Comma-separated additional recipients */
  extraCcEmails: string;
  /** Optional email subject (used when delivery is implemented) */
  emailSubject: string;
  /** Optional email message/body intro (used when delivery is implemented) */
  emailMessage: string;
  /** How the report aggregates figures in the PDF */
  reportGranularity: 'daily' | 'weekly' | 'monthly';
  /** How often to send */
  frequency: 'daily' | 'weekly';
  /** 0 = Sunday … 6 = Saturday (used when frequency is weekly) */
  dayOfWeek: number;
  /** Local send time HH:mm */
  sendTimeLocal: string;
  /** IANA timezone for schedule interpretation */
  timezone: string;
  /** If true, include the Brief as page 1 of the attached PDF */
  attachBrief: boolean;
  attachPdf: boolean;
  attachCsv: boolean;
  /** Which CSV exports to attach/download */
  csvExports: {
    vehicleLevel: boolean;
    orderLevel: boolean;
    customerLevel: boolean;
  };
  /** Which date range each run should cover */
  periodCoverage:
    | 'previous_week'
    | 'previous_month'
    | 'month_to_date'
    | 'last_7_days'
    | 'last_30_days';

  /** Which report sections to include in the PDF */
  sections: {
    executiveSnapshot: boolean;
    fleetKpis: boolean;
    vehicles: boolean;
    periods: boolean;
    customers: boolean;
    routes: boolean;
  };

  /** Filters to apply for scheduled outputs (independent of current UI view) */
  filters: {
    hideStIvesVehicles: boolean;
    vehicleIds: string[];
  };
}

export interface AppConfig {
  telematics: TelematicsConfig;
  profitability: ProfitabilityConfig;
  profitabilityReportSchedule: ProfitabilityReportScheduleConfig;
}

const defaultConfig: AppConfig = {
  telematics: {
    latestIntervalDisplay: '1h',
  },
  profitability: {
    costRateBasis: 'body_style',
    mileageRate3_5T: 0.35,
    mileageRate7_5T: 0.45,
    mileageRate18T: 0.52,
    mileageRate26T: 0.58,
    mileageRateArtic: 0.61,
    hourlyRate3_5T: 30,
    hourlyRate7_5T: 35,
    hourlyRate18T: 38,
    hourlyRate26T: 40.97,
    hourlyRateArtic: 47.59,
    workingHourlyRate3_5T: 16,
    workingHourlyRate7_5T: 18,
    workingHourlyRate18T: 18.5,
    workingHourlyRate26T: 20,
    workingHourlyRateArtic: 20,
    workingHourlyRate: 20,
    maxDailyHours: 11,
    fuelCostLorry: 0.85,
    fuelCostTractorUnit: 0.95,
    fuelCostMiniTruck: 0.2,
    fuelCostServiceVan: 0.17,
    fuelCostRigidTruck: 0.7,
    fuelCostDefault: 0.65,
    fuelCost3_5T: 0.3,
    fuelCost7_5T: 0.45,
    fuelCost18T: 0.7,
    fuelCost26T: 0.85,
    fuelCostArtic: 0.95,
    depreciationLorry: 70,
    depreciationTractorUnit: 70,
    depreciationMiniTruck: 70,
    depreciationServiceVan: 70,
    depreciationRigidTruck: 70,
    depreciationDefault: 70,
    depreciation3_5T: 45,
    depreciation7_5T: 55,
    depreciation18T: 65,
    depreciation26T: 70,
    depreciationArtic: 85,
  },
  profitabilityReportSchedule: {
    enabled: false,
    recipientEmail: '',
    recipientName: '',
    extraCcEmails: '',
    emailSubject: '',
    emailMessage: '',
    reportGranularity: 'daily',
    frequency: 'weekly',
    dayOfWeek: 1,
    sendTimeLocal: '09:00',
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'Europe/London',
    attachBrief: false,
    attachPdf: true,
    attachCsv: false,
    csvExports: {
      vehicleLevel: true,
      orderLevel: false,
      customerLevel: false,
    },
    periodCoverage: 'previous_week',
    sections: {
      executiveSnapshot: true,
      fleetKpis: true,
      vehicles: true,
      periods: true,
      customers: true,
      routes: true,
    },
    filters: {
      hideStIvesVehicles: true,
      vehicleIds: [],
    },
  },
};

function loadConfig(): AppConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultConfig;
    const parsed = JSON.parse(raw) as Partial<AppConfig>;
    return {
      telematics: { ...defaultConfig.telematics, ...parsed.telematics },
      profitability: { ...defaultConfig.profitability, ...parsed.profitability },
      profitabilityReportSchedule: {
        ...defaultConfig.profitabilityReportSchedule,
        ...(parsed.profitabilityReportSchedule ?? {}),
      },
    };
  } catch {
    return defaultConfig;
  }
}

function saveConfig(config: AppConfig) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.warn('Failed to save app config', e);
  }
}

interface ConfigContextType {
  config: AppConfig;
  setTelematics: (patch: Partial<TelematicsConfig>) => void;
  setProfitability: (patch: Partial<ProfitabilityConfig>) => void;
  setProfitabilityReportSchedule: (patch: Partial<ProfitabilityReportScheduleConfig>) => void;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfigState] = useState<AppConfig>(loadConfig);

  useEffect(() => {
    saveConfig(config);
  }, [config]);

  /** Merge server-persisted profitability schedule (retry once after login may complete). */
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.profitabilityReportSchedule());
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as {
          profitabilityReportSchedule?: ProfitabilityReportScheduleConfig | null;
        };
        if (!data?.profitabilityReportSchedule || cancelled) return;
        setConfigState((prev) => ({
          ...prev,
          profitabilityReportSchedule: {
            ...prev.profitabilityReportSchedule,
            ...data.profitabilityReportSchedule,
          },
        }));
      } catch {
        /* not signed in or server offline — keep localStorage schedule */
      }
    };
    void hydrate();
    const t = window.setTimeout(() => void hydrate(), 4000);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, []);

  const setTelematics = useCallback((patch: Partial<TelematicsConfig>) => {
    setConfigState((prev) => ({
      ...prev,
      telematics: { ...prev.telematics, ...patch },
    }));
  }, []);

  const setProfitability = useCallback((patch: Partial<ProfitabilityConfig>) => {
    setConfigState((prev) => ({
      ...prev,
      profitability: { ...prev.profitability, ...patch },
    }));
  }, []);

  const setProfitabilityReportSchedule = useCallback((patch: Partial<ProfitabilityReportScheduleConfig>) => {
    setConfigState((prev) => ({
      ...prev,
      profitabilityReportSchedule: { ...prev.profitabilityReportSchedule, ...patch },
    }));
  }, []);

  return (
    <ConfigContext.Provider
      value={{ config, setTelematics, setProfitability, setProfitabilityReportSchedule }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

